/**
 * AWARE Health — KpiCard Component
 * Single-value stat card for the dashboard KPI row.
 * Theme-aware: adapts colors based on light/dark mode.
 */

import type { ReactNode } from 'react'
import { useTheme } from '../../contexts/ThemeContext'

interface KpiCardProps {
  label: string
  value: ReactNode
  sub?: string
  accent?: string
  icon?: string
  className?: string
}

export default function KpiCard({
  label,
  value,
  sub,
  accent = '#14b8a6',
  icon,
  className = '',
}: KpiCardProps) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div
      className={`kpi-card glass-card rounded-xl p-4 flex flex-col gap-2 min-w-0 ${className}`}
      style={{
        borderTop: `2px solid ${accent}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon && (
          <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
        )}
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: isDark ? '#64748b' : '#94a3b8',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
      </div>

      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: accent,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1.1,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </div>

      {sub && (
        <div
          style={{
            fontSize: 11,
            color: isDark ? '#475569' : '#94a3b8',
            marginTop: -2,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  )
}
